/**
 * The display name for a pipeline stage, as the product calls it.
 *
 * `token_usage.stage` stores the skill slug (`scout-skill`). Two screens used to
 * carry their own copy of this map and both had drifted: Analytics was missing
 * nine of the eighteen slugs the code can emit and the Token Usage panel ten, so
 * a piece scored by the voice evaluator reported its cost against
 * "voice-evaluator-skill" while everything beside it read "Writer", "Librarian".
 *
 * Founder-reported. The fix is one map, and a fallback that degrades to a
 * readable name rather than a slug — a new skill should never again ship a raw
 * identifier into a screen just because nobody remembered this file.
 */
const STAGE_LABELS: Record<string, string> = {
  // Main pipeline, in run order.
  "scout-skill": "Scout",
  "librarian-skill": "Librarian",
  "writer-skill": "Writer",
  "auditor-skill": "Auditor",
  "editor-skill": "Editor",
  "voice-evaluator-skill": "Voice Evaluator",
  "seo-optimizer-skill": "SEO",
  "art-director-skill": "Art Director",
  "publisher-skill": "Publisher",
  "social-amplifier-skill": "Amplifier",

  // Newsletter pipeline. Qualified, because "Editor" and "Writer" already mean
  // the main pipeline's agents and a cost table that lists both unqualified is
  // worse than one that lists neither.
  "newsletter-curator-skill": "Newsletter Curator",
  "newsletter-writer-skill": "Newsletter Writer",
  "newsletter-editor-skill": "Newsletter Editor",

  // Fiction pipeline. These names are unambiguous on their own, so the
  // "fiction-" prefix is dropped rather than repeated in every row.
  "fiction-worldbuilder-skill": "Worldbuilder",
  "fiction-architect-skill": "Architect",
  "fiction-storyteller-skill": "Storyteller",
  "fiction-continuity-skill": "Continuity",
  "fiction-prose-editor-skill": "Prose Editor",
  "fiction-illustrator-skill": "Illustrator",
};

/**
 * Never returns a raw slug. An unmapped stage becomes Title Case with the
 * `-skill` suffix dropped, so the worst case is a name that is merely
 * unpolished rather than one that leaks the codebase's vocabulary.
 */
export function stageLabel(stage: string): string {
  const known = STAGE_LABELS[stage];
  if (known) return known;
  return stage
    .replace(/-skill$/, "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

/** Exported for the test that asserts every slug the code can emit is mapped. */
export const STAGE_LABEL_KEYS = Object.keys(STAGE_LABELS);
